import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  IndexTable,
  InlineStack,
  Pagination,
  Select,
  Text,
  Tooltip,
} from '@shopify/polaris';
import { ViewIcon } from '@shopify/polaris-icons';
import RunDetailModal from './RunDetailModal';
import apiClient from '../utils/api';
import {
  headingsFor, emptyRow, itemCountFor, PageSizeSelect, DEFAULT_PAGE_SIZE,
} from '../utils/table';
import {
  SOURCE_COLUMN_TIP, processedTip, runsFrom, sourceLabel, sourceTip, statusBadge, statusTip,
} from '../utils/runs';
import { friendlyError } from '../utils/sync-error-message';

/**
 * The permanent record of background runs, in one table used from two places.
 *
 * Pass `brandId` for the brand page — every run recorded for that brand, from BOTH systems.
 * Pass nothing for the connector page — every connector run on every account, with an optional
 * retailer filter.
 *
 * The two scopes are NOT the same rows, and neither is a subset of the other. Only two
 * connector run types record a brandId (the per-brand inventory sync, and a tracking push);
 * the other four are per-RETAILER work covering every brand on the account at once, so they
 * record a retailer and no brand. A brand page therefore cannot show an order fetch, and
 * before the connector-page view existed those runs were written permanently and displayed
 * nowhere.
 *
 * THE BRAND VIEW NOW READS TWO SYSTEMS. It used to call the connector's own history route,
 * which knows nothing about billing — so an invoice build or a commission recalculation for
 * the same brand, on the same night, was recorded and shown nowhere, under a heading that
 * reads as "everything that ran". `getBrandRuns` returns both halves in one shape, and the
 * Source column is not decoration: two systems with separate schedules and separate failure
 * modes, listed together with nothing saying which is which, is a list that invites the wrong
 * conclusion about both. The connector-page view is unchanged and still single-source, so it
 * carries no Source column — a column whose every value is the same word is noise.
 *
 * One component rather than two because the columns carry judgements — which count a row's
 * figure actually is, what "Completed" does and does not promise — and a second copy is how
 * the two screens come to answer the same question differently.
 */

/**
 * The Retailer column was REMOVED, from both scopes.
 *
 * It was blank or vague on nearly every row: "—" on every billing run, because billing never
 * talks to a retailer, and "all retailers" on most connector runs, because the work fans out
 * across every account a brand sells on. A column that says nothing specific on nine rows in
 * ten is width spent on a hedge. The retailer is in the run's own detail, where it is one
 * line among the facts that qualify it.
 */
export const HISTORY_COLUMNS = [
  { key: 'type', title: 'Type', tip: 'Which job ran. Each type does a different thing and reports a different figure, which is why the Processed column is labelled per row.' },
  { key: 'status', title: 'Status', tip: 'Completed means the run finished, not that everything in it succeeded — an order or SKU the retailer refused is recorded against that order or SKU, and the job still completes. "Completed with errors" is a run that finished having lost some of what it was given: what it wrote is real, and the figures beside it are short by the rest. Hover a badge for what it means for that row.' },
  { key: 'processed', title: 'Processed', tip: 'What the run actually got through, labelled with the unit that job type reports: orders for an order fetch, SKUs for an inventory or catalogue run, tracking numbers for a tracking push, and pushes queued for a stock comparison. A blank is a job type that keeps no count, not a count of zero.' },
  { key: 'duration', title: 'Duration', tip: 'Wall clock for the run, including time waiting on Shopify or on a lock.' },
  { key: 'error', title: 'Error', tip: 'What went wrong, in plain terms — hover for the original message the system threw, or open the run for both together. An error 2W has no wording for is shown exactly as it was thrown. A per-order or per-SKU refusal by a retailer is recorded on that record instead, so it will not appear here.' },
  { key: 'date', title: 'Date', tip: 'When the job was queued.' },
];

const SOURCE_COLUMN = { key: 'source', title: 'Source', tip: SOURCE_COLUMN_TIP };

/**
 * Brand leads the connector-wide view and is absent from a brand's own.
 *
 * On a brand page every row carries the same brand name, so the column is one value repeated
 * down the screen. On the Background Jobs page it is the only thing telling two brands' runs
 * apart, so it cannot be dropped outright — it is scoped instead.
 */
const BRAND_COLUMN = { key: 'brand', title: 'Brand / Retailer', tip: 'Who the run was for. Most background work is per RETAILER and covers every brand on that account at once, so those rows name the retailer instead of a brand — the column was showing a dash for all of them, which read as missing data rather than as the fact it is.' };

/** The eye. Everything the row cannot hold — the full error, the times, the attempts. */
const ACTION_COLUMN = { key: 'action', title: 'Action', tip: 'Open the full record of this run: what it did, what it got through, how long it took, and the whole message if it failed.' };

/**
 * Source leads the row, and only on the brand view.
 *
 * First because it is the column that tells a reader which of two systems they are looking at,
 * and every other cell means something slightly different depending on the answer — "all
 * retailers" on a connector run, a unit billing defines itself, a retry limit set by whichever
 * side queued it.
 */
/**
 * Source is on BOTH views now.
 *
 * It was left off the global one on the reasoning that every row there came from the
 * connector, so a column of one repeated word would be noise. True until that view started
 * reading billing too — at which point the absent column became the opposite problem: two
 * systems listed together with nothing saying which is which.
 */
export const columnsFor = (brandScoped) => [
  SOURCE_COLUMN,
  ...HISTORY_COLUMNS.slice(0, 1),
  ...(brandScoped ? [] : [BRAND_COLUMN]),
  ...HISTORY_COLUMNS.slice(1),
  ACTION_COLUMN,
];

/**
 * What a row's one count actually means, by job type.
 *
 * Returns null where the type genuinely records nothing, so the cell can say so rather than
 * print a 0 that reads as "did nothing".
 *
 * INVENTORY_RECONCILE is here and must stay here. It writes skusProcessed like the others but
 * had no case, so every stock comparison showed "—" under a tooltip claiming the type records
 * no count — while the number it records is the one thing that run produces. Its unit is
 * "queued", not "SKUs": a comparison sends nothing, it only queues pushes for the flush.
 */
export const processedFor = (job) => {
  // INVENTORY_SYNC is written by two handlers doing opposite halves. With a retailer account
  // it is the flush, which SENDS. With none it is the per-brand run behind a Shopify stock
  // change, which only QUEUES — the flush sends later, and only while the inventory push is
  // switched on. Both read "1 SKUs" until now, so a change parked in the outbox looked
  // exactly like one delivered to a retailer.
  if (job.jobType === 'INVENTORY_SYNC' && !job.marketplaceId) {
    return { value: job.skusProcessed ?? 0, unit: 'queued' };
  }
  switch (job.jobType) {
    case 'ORDER_SYNC':
      return { value: job.ordersProcessed ?? 0, unit: 'orders' };
    // CATALOGUE_SYNC is in this group, not missing from the switch. It started recording a
    // row in the same change that added it here; left out, it renders blank under a tooltip
    // claiming the type keeps no count — a false statement about a run that counts, and the
    // same defect INVENTORY_RECONCILE carried below.
    case 'INVENTORY_SYNC':
    case 'SKU_MAP_REFRESH':
    case 'CATALOGUE_SYNC':
      return { value: job.skusProcessed ?? 0, unit: 'SKUs' };
    case 'INVENTORY_RECONCILE':
      return { value: job.skusProcessed ?? 0, unit: 'queued' };
    case 'TRACKING_PUSH':
      return { value: job.trackingPushed ?? 0, unit: 'pushed' };
    default:
      return null;
  }
};

/**
 * Plain English for the connector's own job types.
 *
 * The brand view gets these from the server, which is the only place they should be decided —
 * but the connector-wide route predates that and returns raw rows, so the same words are
 * needed here until it is migrated. Kept identical to CONNECTOR_LABELS in
 * modules/connector/services/brand-runs-service.js: two lists that drift are how one run gets
 * two names on two screens.
 */
const CONNECTOR_LABELS = {
  ORDER_SYNC: 'Order fetch',
  SKU_MAP_REFRESH: 'Retailer catalogue refresh',
  INVENTORY_SYNC: 'Stock push',
  INVENTORY_RECONCILE: 'Stock reconcile',
  TRACKING_PUSH: 'Tracking push',
  MARKETPLACE_VERIFY: 'Retailer credential check',
  FULL_SYNC: 'Full sync',
  CATALOGUE_SYNC: 'Shopify catalogue read',
  LEDGER_PRUNE: 'Record cleanup',
};

/**
 * The tone of the whole ROW, which is not the badge's tone.
 *
 * A badge colours every status — 'attention' for a run still going — but a toned row is a
 * claim that something is wrong with it, and a table where most rows are toned tones nothing.
 * Only the two losses qualify: red where nothing got through, amber where some of it did.
 */
const rowTone = (row) => {
  if (row?.status === 'FAILED') return 'critical';
  if (row?.status === 'COMPLETED' && row?.partial) return 'warning';
  return undefined;
};

/**
 * A retailer-less row does not mean the same thing on both sides, so it must not read the same.
 *
 * A connector run with no account fanned out across every retailer the brand sells on — "all
 * retailers" is the honest label and has been since this table shipped. A BILLING run has no
 * retailer because billing never talks to one; labelling it "all retailers" would claim it
 * reached every account, which is the opposite of true.
 */
const retailerCell = (source, retailerName) => {
  if (retailerName) return { text: retailerName, tip: null };
  if (source === 'billing') {
    return {
      text: '—',
      tip: 'Billing work is not scoped to a retailer account — it reads orders already in 2W. '
        + 'A blank here is the job type, not missing data.',
    };
  }
  return {
    text: 'all retailers',
    tip: 'This run fanned out across every retailer the brand is connected to, so it was '
      + 'recorded against no single account.',
  };
};

/**
 * The two payloads, one row.
 *
 * Both tables below this line read the SAME fields, so the branch between an old connector
 * history row and a merged run row happens exactly once, here. The alternative — a cell that
 * reads `job.duration ?? run.durationMs` — is how a column ends up rendering correctly on one
 * screen and blank on the other with no error anywhere.
 */
const fromMergedRun = (run) => {
  const { text, tip } = retailerCell(run.source, run.retailerName);
  return {
    id: run.id,
    source: run.source || 'connector',
    type: run.type || null,
    // The server's plain-English label, with the raw enum kept for the hover because the enum
    // is what appears in the logs someone will grep next.
    label: run.label || String(run.type || '').replace(/_/g, ' '),
    status: run.status,
    // Beside the status, never folded into it. The server keeps `status` at COMPLETED on a
    // short run — the run did finish — and sends this as the second fact; the badge and the
    // row tone are the only places the pair is turned back into one thing a reader sees.
    partial: run.partial === true,
    retailerText: text,
    retailerTip: tip,
    // The retailer when there is no brand, because most runs are per retailer and are
    // recorded against no brand at all. A dash on every one of those rows read as data we
    // had failed to load rather than as how the work is scoped.
    brandText: run.brandName || run.retailerName || '—',
    processed: run.processed || null,
    durationMs: run.durationMs ?? null,
    error: run.error || null,
    attempts: run.attempts ?? null,
    maxAttempts: run.maxAttempts ?? null,
    createdAt: run.createdAt,
    startedAt: run.startedAt ?? null,
    completedAt: run.completedAt ?? null,
    note: run.note || null,
  };
};

const fromConnectorHistory = (job) => {
  const { text, tip } = retailerCell('connector', job.marketplace?.name);
  return {
    id: job.id,
    source: 'connector',
    type: job.jobType || null,
    // The merged route sends a plain-English label; this one sends the raw enum, and
    // "SKU MAP REFRESH" was reaching the screen on the connector-wide view while the brand
    // view showed "Retailer catalogue refresh" for the identical run.
    label: CONNECTOR_LABELS[job.jobType] || String(job.jobType || '').replace(/_/g, ' '),
    status: job.status,
    // Read off the RAW job row here, because this route predates the merged shape and returns
    // the ledger row as it is stored. Left out, the identical run would read "Completed with
    // errors" on a brand page and plain green on the connector-wide view — the same defect
    // the labels above exist to prevent, on the column that matters most.
    partial: job.partial === true,
    retailerText: text,
    retailerTip: tip,
    brandText: job.brand?.name || job.marketplace?.name || '—',
    processed: processedFor(job),
    durationMs: job.duration ?? null,
    error: job.error || null,
    // This route records neither, and saying so with null is what lets statusTip leave the
    // sentence out instead of printing "attempt null".
    attempts: null,
    maxAttempts: null,
    createdAt: job.createdAt,
    startedAt: null,
    completedAt: null,
    note: null,
  };
};

export const toHistoryRows = (payload, { merged }) => runsFrom(payload)
  .map((row) => (merged ? fromMergedRun(row) : fromConnectorHistory(row)));

export default function SyncHistoryTable({
  brandId = null,
  marketplaces = [],
  refreshToken = 0,
  emptyMessage = null,
}) {
  const brandScoped = Boolean(brandId);

  // '' is "every account", and it is the default: this table exists to show the runs a
  // brand-scoped view cannot, so opening it already filtered would hide half of them.
  const [marketplaceId, setMarketplaceId] = useState('');
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  // The run whose detail is open, or null. Holding the ROW rather than an id: the dialog
  // shows what this table already fetched, so opening it costs no second request and cannot
  // disagree with the row it was opened from.
  const [openRun, setOpenRun] = useState(null);

  const load = useCallback(async () => {
    try {
      // BOTH views read the merged route now. The global one used to read a connector-only
      // route, so every billing run — a different system, its own schedule, its own failures —
      // was recorded and shown nowhere, under a heading that reads as everything that ran.
      // That is the same fault this table was built to fix, left in place one level up.
      const data = brandId
        ? await apiClient.getBrandRuns(brandId, { scope: 'history' })
        : await apiClient.getRuns({ scope: 'history', marketplaceId: marketplaceId || undefined });
      // Merged either way now — both routes return the same row shape, so the legacy
      // connector-only normaliser is no longer reachable from this screen.
      setRows(toHistoryRows(data, { merged: true }));
      setError(null);
    } catch (err) {
      // The rows already on screen are left alone. A failed refresh of a permanent record does
      // not mean the record emptied, and blanking the table would say it did — the banner says
      // what actually happened.
      setError(err.message || 'Could not load the sync history');
    }
  }, [brandId, marketplaceId]);

  useEffect(() => { load(); }, [load, refreshToken]);

  // Paged CLIENT-side: the record only grows, and both endpoints return one capped list
  // rather than a page. Any filter change returns to page 1 or a narrower list lands on a
  // page that no longer exists and reads as "nothing ran".
  useEffect(() => { setPage(1); }, [marketplaceId, pageSize]);

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const visible = rows.slice((safePage - 1) * pageSize, safePage * pageSize);

  const columns = useMemo(() => columnsFor(brandScoped), [brandScoped]);
  const headings = useMemo(() => headingsFor(columns), [columns]);

  return (
    <BlockStack gap="300">
      {error && <Banner tone="critical">{error}</Banner>}

      {/* Only where there is a choice to make. The brand page passes no retailers, because
          its rows are selected by brand and a retailer filter there would narrow a list that
          is already scoped by something else. */}
      {!brandScoped && marketplaces.length > 1 && (
        <InlineStack gap="300">
          <Select
            label="Retailer"
            labelHidden
            options={[
              { label: 'All retailers', value: '' },
              ...marketplaces.map((m) => ({ label: m.name, value: m.id })),
            ]}
            value={marketplaceId}
            onChange={setMarketplaceId}
          />
        </InlineStack>
      )}

      {/* itemCount forced to 1 when empty so the headings survive: at 0 IndexTable swaps the
          whole table for emptyState, column names included, and a table with no column names
          reads as broken rather than empty. */}
      <IndexTable
        resourceName={{ singular: 'run', plural: 'runs' }}
        itemCount={itemCountFor(visible)}
        headings={headings}
        selectable={false}
      >
        {visible.length === 0
          ? emptyRow(
            columns,
            'sync-history',
            emptyMessage
              || 'Nothing has run yet. Each row will be one run — what it did, what it got '
                + 'through, and why it stopped if it failed.',
          )
          : visible.map((row, i) => (
            /* Toned on the whole row, not just the Error cell. A loss is a fact about the
               run, and one coloured word at the far right of a wide table is the thing a
               person scanning for trouble misses. Two tones only: red where nothing got
               through, amber where some of it did and the figures beside it are short. A
               note is still not a fault — toning every row makes none of them mean
               anything. */
            <IndexTable.Row
              id={row.id}
              key={row.id}
              position={i}
              tone={rowTone(row)}
            >
              {/* ALWAYS rendered, because the heading is always there. It used to be both
                  conditional together; making the heading unconditional without the cell
                  shifted every column left by one on the global view, so Source showed the
                  type and Type showed the brand. A heading and its cell must be governed by
                  the same condition or by none. */}
              <IndexTable.Cell>
                {/* No tone. Polaris puts a visually-hidden tone word inside a toned badge,
                    so an "info" Source cell reads as "Info Billing" to a screen reader —
                    and this column is an origin, not a severity. */}
                <Tooltip content={sourceTip(row.source)}>
                  <Badge>{sourceLabel(row.source)}</Badge>
                </Tooltip>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <BlockStack gap="050">
                  <Tooltip content={`Job type: ${row.type || 'not recorded'}`}>
                    <Badge>{row.label}</Badge>
                  </Tooltip>
                  {/* The note is the run's own explanation of itself — "skipped, invoice
                      already issued" and the like. It was being dropped, which turned a row
                      that had said why it did nothing into a row that said nothing. */}
                  {row.note && (
                    <Box maxWidth="280px">
                      <Tooltip content={row.note}>
                        <Text variant="bodySm" tone="subdued" truncate>{row.note}</Text>
                      </Tooltip>
                    </Box>
                  )}
                </BlockStack>
              </IndexTable.Cell>
              {/* An em dash, not "all brands". A retailer run does cover every brand on the
                  account, but it was not recorded against any of them, and saying "all
                  brands" would invite someone to look for it on a brand page. */}
              {!brandScoped && (
                <IndexTable.Cell>
                  <Text variant="bodySm" tone="subdued">{row.brandText}</Text>
                </IndexTable.Cell>
              )}
              {/* The badge says "Completed with errors" on a short run, not "COMPLETED". The
                  row is COMPLETED and stays COMPLETED — it finished — but the word on its own
                  is the one people read as "everything worked", which is what let a run lose
                  500 SKUs behind a green badge. */}
              <IndexTable.Cell>
                <Tooltip content={statusTip(row)}>
                  <Badge tone={statusBadge(row).tone}>{statusBadge(row).text}</Badge>
                </Tooltip>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <Tooltip content={processedTip(row.processed)}>
                  {row.processed ? (
                    <Text variant="bodySm">
                      {row.processed.value}
                      {' '}
                      <Text as="span" tone="subdued">{row.processed.unit}</Text>
                    </Text>
                  ) : (
                    <Text variant="bodySm" tone="subdued">—</Text>
                  )}
                </Tooltip>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <Text variant="bodySm">
                  {row.durationMs ? `${(row.durationMs / 1000).toFixed(1)}s` : '—'}
                </Text>
              </IndexTable.Cell>
              {/* Truncated in the cell, whole on hover. A failure message can be a stack
                  trace, and a stack trace cut off at the column width is not an error
                  message — it is the first frame of one. */}
              <IndexTable.Cell>
                {row.error ? (
                  /* The Box is what makes `truncate` work. Polaris clips to the CONTAINER,
                     and a table cell has no width of its own — so without a cap the cell grew
                     to fit a 300-character message and dragged the table off screen. The
                     whole text is on hover and in the detail behind the eye. */
                  /* The plain-English line, with the ORIGINAL on hover and in full behind
                     the eye. Translating here rather than on the way into the database keeps
                     the real message intact and lets better wording improve rows that failed
                     before it was written. An unrecognised error shows as thrown. */
                  <Box maxWidth="280px">
                    <Tooltip content={row.error}>
                      <Text variant="bodySm" truncate>{friendlyError(row.error).summary}</Text>
                    </Tooltip>
                  </Box>
                ) : (
                  <Text variant="bodySm" tone="subdued">—</Text>
                )}
              </IndexTable.Cell>
              <IndexTable.Cell>
                <Text variant="bodySm">{new Date(row.createdAt).toLocaleString()}</Text>
              </IndexTable.Cell>
              {/* stopPropagation so opening the detail is not also whatever a row click may
                  come to mean later. The button is the affordance; the row is not. */}
              <IndexTable.Cell>
                <Tooltip content="See everything about this run">
                  <Button
                    size="slim"
                    icon={ViewIcon}
                    accessibilityLabel={`See everything about this ${row.label} run`}
                    onClick={(e) => { e?.stopPropagation?.(); setOpenRun(row); }}
                  />
                </Tooltip>
              </IndexTable.Cell>
            </IndexTable.Row>
          ))}
      </IndexTable>

      {/* Mounted only while open, so closing discards its state and the next row opens clean
          rather than flashing the previous run. */}
      {openRun && <RunDetailModal run={openRun} onClose={() => setOpenRun(null)} />}

      {rows.length > 0 && (
        <InlineStack align="space-between" blockAlign="center">
          <PageSizeSelect value={pageSize} onChange={setPageSize} />
          {totalPages > 1 && (
            <Pagination
              hasPrevious={safePage > 1}
              onPrevious={() => setPage(safePage - 1)}
              hasNext={safePage < totalPages}
              onNext={() => setPage(safePage + 1)}
              label={`Page ${safePage} of ${totalPages} · ${rows.length} run${rows.length === 1 ? '' : 's'}`}
            />
          )}
        </InlineStack>
      )}
    </BlockStack>
  );
}
